import { getWebInstrumentations, initializeFaro } from "@grafana/faro-web-sdk";
import { ReactIntegration } from "@grafana/faro-react";
import { fromEnv } from "./fromEnv";

const FARO_URL = fromEnv("REACT_APP_FARO_URL");
const FARO_APP_NAME = fromEnv("REACT_APP_FARO_APP_NAME");
const FARO_API_KEY = fromEnv("REACT_APP_FARO_API_KEY");
const ENVIRONMENT = fromEnv("REACT_APP_ENVIRONMENT");

let faroInstance: any = null;

const ensureInitialized = () => {
  if (faroInstance) {
    return faroInstance;
  }

  if (!FARO_URL || !FARO_APP_NAME) {
    console.warn("[Faro] Skipped - missing configuration");
    return null;
  }

  try {
    faroInstance = initializeFaro({
      url: FARO_URL,
      apiKey: FARO_API_KEY,
      app: {
        name: FARO_APP_NAME,
        environment: ENVIRONMENT,
      },
      instrumentations: [...getWebInstrumentations(), new ReactIntegration()],
    });
    console.log(`[Faro] Initialized in ${ENVIRONMENT} environment`);
    return faroInstance;
  } catch (error) {
    console.error("[Faro] Initialization failed:", error);
    return null;
  }
};

const createFaroLogger = () => {
  return {
    info: (...messages: any[]) => {
      if (process.env.NODE_ENV === "development") {
        console.log("Logger.info", ...messages);
      }
      const instance = ensureInitialized();
      if (instance?.api) {
        instance.api.pushLog(messages, { level: "info" });
      }
    },

    warn: (content: any) => {
      if (process.env.NODE_ENV === "development") {
        const stack = new Error().stack;
        console.warn("Logger.warn", { content, stack });
      }
      const instance = ensureInitialized();
      if (instance?.api) {
        instance.api.pushError(new Error(String(content)), {
          type: "warning",
        });
      }
    },

    error: (error: Error, tags: string[] = []) => {
      if (process.env.NODE_ENV === "development") {
        console.error("Logger.error", error, tags);
      }
      const instance = ensureInitialized();
      if (instance?.api) {
        instance.api.pushError(error, {
          type: "error",
          context: { tags },
        });
      }
    },
  };
};

export const loggerSetUser = (userInfo: {
  id: string;
  email?: string;
  name?: string;
}) => {
  try {
    const instance = ensureInitialized();
    if (instance?.api) {
      instance.api.setUser({
        id: userInfo.id,
        email: userInfo.email,
        attributes: {
          name: userInfo.name,
        },
      });
    }
  } catch (error) {
    console.error("[Faro] Failed to set user:", error);
  }
};

export const loggerSend = (error: Error, options: { tags?: string[] } = {}) => {
  try {
    const instance = ensureInitialized();
    if (instance?.api) {
      instance.api.pushError(error, {
        type: "error",
        context: { tags: options.tags || [] },
      });
    }
  } catch (faroError) {
    console.error("[Faro] Failed to send error:", faroError);
  }
};

export const Logger = createFaroLogger();
